import { useEffect, useState } from "react";
import useAudioPlayer from "../../../../hooks/useAudioPlayer";
import { VoiceNoteViewProps } from "./VoiceNoteSection.types";
import PauseIcon from "../../../../media/PauseIcon";
import PlayIcon from "../../../../media/PlayIcon";
import SkeletonLoadingView from "../../../../components/SkeletonLoadingView/SkeletonLoadingView";
import UserComment from "../../../../components/UserComment/UserComment";
import "./VoiceNoteSection.css"
import Button from "../../../../components/Button/Button";
import { MAX_COMMENT_CHARACTERS } from "../../../../utils/constants";
import ErrorIcon from "../../../../media/ErrorIcon";
import { STARAPIError } from "../../../../service/service";

const VoiceNoteSection = (props: VoiceNoteViewProps) => {
  const {
    scanId,
    voiceNoteSrc,
    voiceNote,
    onLoadVoiceNoteText,
    onEditVoiceNoteText
  } = props

  const [{
    audioRef,
    currentTime,
    duration,
    isPlaying,
    onPlay,
    onPause,
    onCancel
  }] = useAudioPlayer()

  const [isEditing, setisEditing] = useState<boolean>(false)
  const [editedVoiceNote, setEditedVoiceNote] = useState<string>("")
  const [validationError, setValidationError] = useState<string|null>(null)

  useEffect(() => {
    onCancel()
    if (scanId === undefined) {
      return
    }
    onLoadVoiceNoteText(scanId)
  }, [scanId])

  const onClickComments = () => {
    if (voiceNote.status !== "success") { return }
    setEditedVoiceNote(voiceNote.data?.text ?? "")
    setisEditing(true)
    setValidationError(null)
  }

  const isValidTranscriptionEdit = () => {
    if (editedVoiceNote === "") {
      setValidationError("Cannot save empty voice note transcription.")
      return false
    }
    if (editedVoiceNote.length > MAX_COMMENT_CHARACTERS) {
      setValidationError(`Cannot save a voice note transcription with over ${MAX_COMMENT_CHARACTERS} characters.`)
      return false
    }
    return true
  }
  
  const onSaveEdit = () => {
    if (scanId === undefined) { return }
    if (!isValidTranscriptionEdit()) { return }

    onEditVoiceNoteText(editedVoiceNote)
    onLoadVoiceNoteText(scanId)
    setisEditing(false)
  }

  if (voiceNote.status === "error") {
    const error = voiceNote.error as STARAPIError

    if (error.statusCode === 404) {
      return (
        <></>
      )
    }
    return (
      <div className="analysis-section voice-note-error-section">
        <ErrorIcon />
        <p>Failed to load voice note text.</p>
      </div>
    )
  }

  if (voiceNote.status === "loading") {
    return (
      <SkeletonLoadingView style={{height: 120 }} />
    )
  }

  if (voiceNote.status === "idle") {
    return <></>
  }

  return (
    <div className="analysis-section voice-note-container">
      {!isEditing ? (
        <UserComment
          onClick={onClickComments}
          username={voiceNote.data?.user.username ?? ""}
          comment={voiceNote.data?.text ?? ""}
        />
      ) : (
        <div className="edit-voice-note-text-container">
          <textarea
            className="edit-voice-note-text"
            value={editedVoiceNote}
            onChange={(e) => setEditedVoiceNote(e.target.value)}
            placeholder="Update voice note transcription"
          />
          {validationError !== null && (
            <p className="transcript-edit-error">{validationError}</p>
          )}
          <div className="edit-buttons-container">
            <Button title="Save" onClick={onSaveEdit}/>
            <Button title="Cancel" onClick={() => setisEditing(false)} type="outline" />
            {/* <div style={{marginLeft: "auto"}}>
              <Button title="Revert" onClick={onRevertEdits} />
            </div> */}
          </div>
        </div>
      )}

      <div className="audio-player-container">
        {isPlaying ? (
          <div className="voice-comment-play-button" onClick={onPause}>
            <PauseIcon />
          </div>
        ) : (
          <div className="voice-comment-play-button" onClick={onPlay}>
            <PlayIcon />
          </div>
        )}

        <div className="audio-background-bar">
          <div className="audio-progress" style={{width: `${(currentTime/duration)*100}%`}}></div>
        </div>
        <audio ref={audioRef} src={voiceNoteSrc} />
      </div>
      
    </div>
  )
}

export default VoiceNoteSection